import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Search, ShieldAlert, ArrowUpDown, ExternalLink } from "lucide-react";
import { listBannedAccounts, BAN_CATEGORIES, type BannedAccountRow } from "@/lib/user-reports.functions";

export const Route = createFileRoute("/banned-accounts")({
  head: () => ({
    meta: [
      { title: "Banned Accounts — Want To Buy" },
      {
        name: "description",
        content:
          "Public list of usernames permanently banned from the Want To Buy connection platform, with violation category and ban date.",
      },
    ],
  }),
  component: BannedAccountsPage,
});

type SortKey = "username" | "violation_category" | "banned_at";
type SortDir = "asc" | "desc";

function BannedAccountsPage() {
  const fetchFn = useServerFn(listBannedAccounts);
  const { data, isLoading } = useQuery({
    queryKey: ["banned-accounts"],
    queryFn: () => fetchFn(),
  });

  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("banned_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const rows = useMemo<BannedAccountRow[]>(() => {
    const list = data?.entries ?? [];
    const filtered = q.trim()
      ? list.filter((r) => r.username.toLowerCase().includes(q.trim().toLowerCase()))
      : list;
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === "banned_at") {
        cmp = new Date(a.banned_at).getTime() - new Date(b.banned_at).getTime();
      } else if (sortKey === "username") {
        cmp = a.username.localeCompare(b.username);
      } else {
        cmp = a.violation_category.localeCompare(b.violation_category);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [data, q, sortKey, sortDir]);

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(k);
      setSortDir(k === "banned_at" ? "desc" : "asc");
    }
  }

  return (
    <AppShell>
      <div className="mb-6">
        <span className="status-pill bg-destructive/15 text-destructive border border-destructive/30">
          <ShieldAlert className="h-3 w-3" /> Trust &amp; Safety
        </span>
        <h1 className="mt-2 font-display text-3xl font-bold">Banned Accounts</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Permanently banned usernames on Want To Buy. Use the search bar to check a username before transacting. Only
          permanently banned accounts appear here — suspended, temporarily restricted, or accounts with active strikes
          but no ban do not appear on this list.
        </p>
      </div>

      <div className="mb-4 panel flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="text-sm">
          <div className="font-display text-base font-bold">Believe a ban was applied in error?</div>
          <p className="text-xs text-muted-foreground">
            Submit an appeal — bans are re-reviewed by the Trust &amp; Safety team.
          </p>
        </div>
        <Link
          to="/contact"
          search={{ topic: "ban_appeal" } as never}
          className="btn-primary inline-flex items-center gap-1 px-3 py-2 text-xs"
        >
          Contest a ban <ExternalLink className="h-3 w-3" />
        </Link>
      </div>

      <div className="panel mb-4 p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a username before transacting…"
            className="h-10 w-full rounded-md border border-border bg-background pl-9 pr-3 text-sm focus:border-primary focus:outline-none"
          />
        </div>
      </div>

      <div className="panel overflow-hidden">
        <div className="grid grid-cols-[1.5fr_1.5fr_1fr] gap-3 border-b border-border bg-muted/30 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground">
          <SortHeader label="Username" active={sortKey === "username"} dir={sortDir} onClick={() => toggleSort("username")} />
          <SortHeader
            label="Violation category"
            active={sortKey === "violation_category"}
            dir={sortDir}
            onClick={() => toggleSort("violation_category")}
          />
          <SortHeader
            label="Ban date"
            active={sortKey === "banned_at"}
            dir={sortDir}
            onClick={() => toggleSort("banned_at")}
          />
        </div>
        {isLoading ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">
            {q.trim() ? `No banned accounts match "${q.trim()}".` : "No banned accounts to display."}
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="grid grid-cols-[1.5fr_1.5fr_1fr] items-center gap-3 border-b border-border px-4 py-2.5 text-sm last:border-0"
            >
              <div className="font-mono">@{r.username}</div>
              <div>
                <span className="status-pill bg-destructive/15 text-destructive border border-destructive/30">
                  {BAN_CATEGORIES[r.violation_category] ?? r.violation_category}
                </span>
              </div>
              <div className="text-xs text-muted-foreground">{new Date(r.banned_at).toLocaleDateString()}</div>
            </div>
          ))
        )}
      </div>

      <div className="mt-6 rounded-md border border-border bg-panel/60 p-4 text-xs text-muted-foreground">
        This list contains platform usernames only. Want To Buy does not publish personal identifying information. Bans
        are applied following investigation under our{" "}
        <Link to="/platform-policies" className="text-primary hover:underline">
          Platform Policies
        </Link>
        . To contest a ban use the appeal link above.
      </div>
    </AppShell>
  );
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 text-left ${active ? "text-foreground" : ""}`}
    >
      {label}
      <ArrowUpDown className={`h-3 w-3 ${active ? "text-primary" : "opacity-50"}`} />
      {active && <span className="text-[9px]">{dir === "asc" ? "▲" : "▼"}</span>}
    </button>
  );
}
